// Phase 6 — Lifted Training Module Editor (Master Admin authoring).
//
// Source-of-truth list comes from /api/admin/training-official/modules,
// which surfaces the 12 official modules seeded on backend boot. The
// official skeletons (title, category, subcategories, order, role
// visibility) are always present; admin edits cover body text, hero
// image URL, MP4 / PDF URLs, published toggle, and per-role visibility
// overrides.

import React, { useEffect, useMemo, useState } from "react";
import { Save, Loader2, X, BookOpen, EyeOff, Eye, FileText, Columns } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import AdminFeatureShell from "@/components/admin/AdminFeatureShell";
import Markdown from "@/components/lifted/Markdown";

const ROLE_KEYS = ["affiliate", "driver", "partner", "franchisee"];
const ACCESS_VALUES = ["required", "recommended", "overview", "hidden"];
const ACCESS_COLORS = {
  required:    "bg-lifted-green/15 text-lifted-green border-lifted-green/40",
  recommended: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  overview:    "bg-amber-500/15 text-amber-300 border-amber-500/40",
  hidden:      "bg-white/5 text-lifted-dim border-white/10",
};

export default function TrainingModuleEditor() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selectedSlug, setSelectedSlug] = useState(null);

  const load = useMemo(() => async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/training-official/modules");
      setModules(data.modules || []);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selected = modules.find((m) => m.slug === selectedSlug) || null;

  return (
    <AdminFeatureShell
      brandTag="logo.training.primary"
      breadcrumb="Lifted Training / Authoring"
      title="Module Editor"
      description="Author the 12 official Lifted training modules. Body text, hero image, MP4 video and PDF URLs flow through here. Direct file uploads activate once an S3/Cloudinary key is configured."
      status={`${modules.length} modules`}
      missingCredentials={[
        { env: "S3_* or CLOUDINARY_URL", purpose: "Direct file uploads — URLs work today without this." },
      ]}
      testId="training-module-editor"
    >
      {err && (
        <div className="glass-panel border border-rose-500/30 p-3 text-sm text-rose-200">{err}</div>
      )}

      <div className="grid lg:grid-cols-[1fr_460px] gap-6 items-start">
        <div className="space-y-2" data-testid="training-module-list">
          {loading && (
            <div className="glass-panel p-6 text-center text-xs text-lifted-dim">
              <Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" />
              Loading modules…
            </div>
          )}
          {!loading && modules.length === 0 && (
            <div className="glass-panel p-6 text-center text-sm text-lifted-dim">
              No modules found.
            </div>
          )}
          {modules.map((m) => (
            <button
              key={m.slug}
              onClick={() => setSelectedSlug(m.slug)}
              className={`w-full glass-panel p-4 text-left transition ${
                selectedSlug === m.slug ? "border-lifted-green/40" : "hover:border-white/20"
              }`}
              data-testid={`training-module-row-${m.slug}`}
            >
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-md bg-lifted-green/10 text-lifted-green font-bebas tracking-wider grid place-items-center shrink-0">
                  {m.order}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-lifted-text truncate">{m.title}</div>
                  <div className="text-[0.65rem] text-lifted-dim mt-0.5">{m.category}</div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {ROLE_KEYS.map((role) => {
                      const a = (m.role_visibility || {})[role] || "hidden";
                      return (
                        <span
                          key={role}
                          className={`text-[0.55rem] tracking-[0.18em] font-bebas uppercase border rounded-full px-1.5 py-0.5 ${ACCESS_COLORS[a]}`}
                          title={`${role}: ${a}`}
                        >
                          {role[0].toUpperCase()}·{a[0].toUpperCase()}
                        </span>
                      );
                    })}
                    {!m.published && (
                      <span className="text-[0.55rem] tracking-[0.18em] font-bebas uppercase border rounded-full px-1.5 py-0.5 bg-rose-500/15 text-rose-300 border-rose-400/40">
                        UNPUBLISHED
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="lg:sticky lg:top-0">
          {selected ? (
            <EditorPanel
              key={selected.slug}
              module={selected}
              onClose={() => setSelectedSlug(null)}
              onSaved={(updated) => setModules((ms) => ms.map((m) => m.slug === updated.slug ? updated : m))}
            />
          ) : (
            <div className="glass-panel p-6 text-sm text-lifted-dim text-center">
              <BookOpen className="h-5 w-5 text-lifted-green mx-auto mb-2" />
              Select a module to author.
            </div>
          )}
        </div>
      </div>
    </AdminFeatureShell>
  );
}

function EditorPanel({ module: mod, onSaved, onClose }) {
  const [draft, setDraft] = useState(() => ({
    title: mod.title || "",
    category: mod.category || "",
    subcategories: (mod.subcategories || []).join("\n"),
    body: mod.body || "",
    hero_image_url: mod.hero_image_url || "",
    video_url: mod.video_url || "",
    pdf_url: mod.pdf_url || "",
    published: mod.published !== false,
    role_visibility: { ...(mod.role_visibility || {}) },
  }));
  const [bodyMode, setBodyMode] = useState("split");  // edit | preview | split
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const setRoleAccess = (role, value) => {
    setDraft((d) => ({ ...d, role_visibility: { ...d.role_visibility, [role]: value } }));
  };

  const save = async () => {
    setBusy(true); setErr(""); setOk(false);
    try {
      const payload = {
        title: draft.title,
        category: draft.category,
        subcategories: draft.subcategories.split("\n").map((s) => s.trim()).filter(Boolean),
        body: draft.body,
        hero_image_url: draft.hero_image_url,
        video_url: draft.video_url,
        pdf_url: draft.pdf_url,
        published: !!draft.published,
        role_visibility: draft.role_visibility,
      };
      const { data } = await api.patch(`/admin/training-official/modules/${mod.slug}`, payload);
      setOk(true);
      onSaved?.(data.module);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="glass-panel p-5 space-y-4" data-testid="training-module-editor-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-bebas tracking-[0.22em] text-[0.65rem] text-lifted-green">
            MODULE {mod.order}
          </div>
          <div className="text-sm text-lifted-text">{mod.title}</div>
          <div className="text-[0.65rem] text-lifted-dim font-mono">{mod.slug}</div>
        </div>
        <button onClick={onClose} className="text-lifted-dim hover:text-white" aria-label="Close editor">
          <X className="h-4 w-4" />
        </button>
      </div>

      <FieldText  label="Title"    value={draft.title}    onChange={(v) => setDraft((d) => ({ ...d, title: v }))} />
      <FieldText  label="Category" value={draft.category} onChange={(v) => setDraft((d) => ({ ...d, category: v }))} />

      <div>
        <Label>Subcategories (one per line)</Label>
        <textarea
          value={draft.subcategories}
          onChange={(e) => setDraft((d) => ({ ...d, subcategories: e.target.value }))}
          rows={4}
          className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-xs font-mono focus:border-lifted-green/40 focus:outline-none"
          data-testid="training-module-subcategories"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <Label>Body (markdown)</Label>
          <BodyModeToggle value={bodyMode} onChange={setBodyMode} />
        </div>
        {bodyMode === "edit" && (
          <textarea
            value={draft.body}
            onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
            rows={10}
            placeholder="# Lesson title&#10;Use **bold**, *italics*, lists, > quotes, [links](https://example.com)."
            className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:border-lifted-green/40 focus:outline-none"
            data-testid="training-module-body"
          />
        )}
        {bodyMode === "preview" && (
          <div className="bg-black/50 border border-white/10 rounded-md px-4 py-3 min-h-[180px] max-h-[420px] overflow-y-auto"
               data-testid="training-module-body-preview">
            {draft.body ? <Markdown source={draft.body} /> : <div className="text-xs text-lifted-dim">Nothing to preview yet.</div>}
          </div>
        )}
        {bodyMode === "split" && (
          <div className="grid grid-cols-2 gap-2">
            <textarea
              value={draft.body}
              onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
              rows={14}
              placeholder="# Lesson title…"
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-xs font-mono focus:border-lifted-green/40 focus:outline-none"
              data-testid="training-module-body"
            />
            <div className="bg-black/50 border border-white/10 rounded-md px-3 py-2 overflow-y-auto" style={{ maxHeight: 360 }}>
              {draft.body ? <Markdown source={draft.body} /> : <div className="text-xs text-lifted-dim">Live preview appears here.</div>}
            </div>
          </div>
        )}
      </div>

      <FieldText label="Hero image URL"  value={draft.hero_image_url} onChange={(v) => setDraft((d) => ({ ...d, hero_image_url: v }))} />
      <FieldText label="Video / MP4 URL" value={draft.video_url}      onChange={(v) => setDraft((d) => ({ ...d, video_url: v }))} />
      <FieldText label="PDF / Doc URL"   value={draft.pdf_url}        onChange={(v) => setDraft((d) => ({ ...d, pdf_url: v }))} />

      <div>
        <Label>Per-role visibility</Label>
        <div className="space-y-1.5">
          {ROLE_KEYS.map((role) => (
            <div key={role} className="flex items-center gap-2">
              <span className="text-xs text-lifted-text capitalize w-24">{role}</span>
              <select
                value={draft.role_visibility[role] || "hidden"}
                onChange={(e) => setRoleAccess(role, e.target.value)}
                className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs flex-1"
                data-testid={`training-module-access-${role}`}
              >
                {ACCESS_VALUES.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={() => setDraft((d) => ({ ...d, published: !d.published }))}
        className={`w-full border rounded-md px-3 py-2 text-xs inline-flex items-center gap-2 ${
          draft.published ? "border-lifted-green/40 text-lifted-green bg-lifted-green/10" : "border-white/10 text-lifted-dim"
        }`}
      >
        {draft.published ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
        {draft.published ? "Published" : "Unpublished"}
      </button>

      {err && <div className="text-xs text-rose-300">{err}</div>}
      {ok && <div className="text-xs text-lifted-green">Saved.</div>}

      <button
        onClick={save}
        disabled={busy}
        className="btn-lifted !py-2 !px-4 text-xs inline-flex items-center gap-2 disabled:opacity-50"
        data-testid="training-module-save"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
        Save module
      </button>
    </div>
  );
}

function Label({ children }) {
  return <div className="font-bebas text-[0.6rem] tracking-[0.22em] text-lifted-dim mb-1.5">{children}</div>;
}

function FieldText({ label, value, onChange }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-lifted-green/40 focus:outline-none"
      />
    </div>
  );
}

function BodyModeToggle({ value, onChange }) {
  const opts = [
    { v: "edit",    icon: FileText, label: "Edit"    },
    { v: "preview", icon: Eye,      label: "Preview" },
    { v: "split",   icon: Columns,  label: "Split"   },
  ];
  return (
    <div className="inline-flex border border-white/10 rounded-md overflow-hidden">
      {opts.map((o) => {
        const Ic = o.icon;
        const active = value === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            className={`px-2.5 py-1 text-[0.65rem] font-bebas tracking-[0.18em] inline-flex items-center gap-1 ${
              active ? "bg-lifted-green/15 text-lifted-green" : "text-lifted-dim hover:text-white"
            }`}
            data-testid={`training-body-mode-${o.v}`}
          >
            <Ic className="h-3 w-3" /> {o.label.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}
